"use client";

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useWebSocket } from "@/app/hooks/useWebSocket";
import { TireReadingModel } from "../models/TireReadingModel";
import { createOverwriteRead, approveReadingInstant } from "@/app/api/api";
import { OverwriteType } from "../types/OverwriteType";
import type { WebSocketStatus } from "@/app/hooks/useWebSocket"; // <-- Add this

interface TireReadingContextType {
  codes: string[];
  selected: string | null;
  initial: string | null;
  adjustmentReason: string | null;
  adjustedDepth: number | null;
  currentReading: TireReadingModel | null;
  misreadWarning: boolean;
  connectionStatus: WebSocketStatus; // <-- Add to context
  setCodes: (codes: string[]) => void;
  confirmClassificationChange: (
    code: string | null,
    reason?: string,
    newDepth?: number,
    newWidth?: number,
    newHeight?: number,
    newRim?: number
  ) => void;
  setAdjustmentReason: (reason: string) => void;
  setAdjustedDepth: (depth: number) => void;
  setCurrentReading: (reading: TireReadingModel | null) => void;
  setMisreadWarning: (warning: boolean) => void;
  goToPreviousReading: () => void;
  goToNextReading: () => void;
  readings: TireReadingModel[];
  currentIndex: number;
  setCurrentIndex: (idx: number) => void;
  approveReading: () => Promise<void>;
}

const TireReadingContext = createContext<TireReadingContextType | undefined>(
  undefined
);

interface TireReadingState {
  readings: TireReadingModel[];
  currentIndex: number;
}

type TireReadingAction =
  | { type: "ADD_READING"; reading: TireReadingModel }
  | { type: "SET_INDEX"; index: number }
  | { type: "MARK_CHECKED"; index: number };

function tireReadingReducer(
  state: TireReadingState,
  action: TireReadingAction
): TireReadingState {
  switch (action.type) {
    case "ADD_READING": {
      const prev = state.readings;
      const currentIndex = state.currentIndex;
      const reading = action.reading;
      const isDuplicate = prev.some((r) => r.id === reading.id);
      if (isDuplicate) return state;
      if (prev.length < 20) {
        if (currentIndex === -1) {
          return {
            readings: [...prev, reading],
            currentIndex: prev.length,
          };
        }
        return {
          readings: [...prev, reading],
          currentIndex: state.currentIndex,
        };
      } else {
        const selectedId = prev[currentIndex]?.id;
        const newReadings = [...prev.slice(1), reading];
        if (currentIndex === -1) {
          return {
            readings: newReadings,
            currentIndex: newReadings.length - 1,
          };
        }
        const idx = newReadings.findIndex((r) => r.id === selectedId);
        return {
          readings: newReadings,
          currentIndex: idx !== -1 ? idx : 0,
        };
      }
    }
    case "SET_INDEX": {
      return {
        ...state,
        currentIndex: action.index,
      };
    }
    case "MARK_CHECKED": {
      return {
        ...state,
        readings: state.readings.map((r, i) =>
          i === action.index ? new TireReadingModel(r.data, true) : r
        ),
      };
    }
    default:
      return state;
  }
}

export const TireReadingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(tireReadingReducer, {
    readings: [],
    currentIndex: -1,
  });
  const [codes, setCodes] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [initial, setInitial] = useState<string | null>(null);
  const [adjustmentReason, setAdjustmentReason] = useState<string | null>(null);
  const [adjustedDepth, setAdjustedDepth] = useState<number | null>(null);
  const [misreadWarning, setMisreadWarning] = useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] =
    useState<WebSocketStatus>("connecting");

  // The reading currently being displayed
  const currentReading =
    state.currentIndex >= 0 ? state.readings[state.currentIndex] : null;

  // Navigation functions
  const goToPreviousReading = () => {
    dispatch({ type: "SET_INDEX", index: Math.max(0, state.currentIndex - 1) });
  };
  const goToNextReading = () => {
    dispatch({
      type: "SET_INDEX",
      index: Math.min(state.readings.length - 1, state.currentIndex + 1),
    });
  };
  const setCurrentIndex = (idx: number) => {
    dispatch({ type: "SET_INDEX", index: idx });
  };

  // Set current reading by index (for external use, e.g. after classification)
  const setCurrentReading = (reading: TireReadingModel | null) => {
    if (!reading) {
      setCurrentIndex(-1);
      return;
    }
    dispatch({ type: "ADD_READING", reading });
  };

  // Handle new readings from websocket
  const handleWsMessage = useCallback((data: any) => {
    if (data?.type === "new_read") {
      const reading = TireReadingModel.from(data.read, false); // checked: false
      console.log("New reading received:", reading);

      if (reading.classifier_outcome === "OUT" || !reading.possible_outcomes) {
        reading.data.classifier_outcome = "XX";
        reading.data.possible_outcomes = ["XX"];
      }

      dispatch({ type: "ADD_READING", reading });
    }
  }, []);

  // Show loader if all readings are checked or there are no readings
  const allChecked =
    state.readings.length > 0 && state.readings.every((r) => r.checked);
  useEffect(() => {
    if (
      (state.readings.length > 0 && allChecked) ||
      state.readings.length === 0
    ) {
      dispatch({ type: "SET_INDEX", index: -1 });
    } else if (state.readings.length > 0 && state.currentIndex === -1) {
      // If loader was showing and a new unchecked reading comes in, go to it
      const firstUnchecked = state.readings.findIndex((r) => !r.checked);
      if (firstUnchecked !== -1)
        dispatch({ type: "SET_INDEX", index: firstUnchecked });
    }
  }, [state.readings, allChecked, state.currentIndex]);

  // When currentReading changes, update codes, initial, selected, misreadWarning
  useEffect(() => {
    if (currentReading) {
      setCodes(currentReading.possible_outcomes);
      setInitial(currentReading.classifier_outcome);
      setSelected(currentReading.classifier_outcome);
      setMisreadWarning(
        currentReading.classifier_outcome === "XX" &&
          currentReading.possible_outcomes?.length === 1
      );
    }
  }, [currentReading]);

  const wsStatus = useWebSocket(
    "wss://granu-api.d3cisive.com/api/v1/ws",
    handleWsMessage
  );

  // Sync wsStatus to internal state
  useEffect(() => {
    setConnectionStatus(wsStatus);
  }, [wsStatus]);

  const confirmClassificationChange = async (
    code: string | null,
    reason?: string,
    newDepth?: number,
    newWidth?: number,
    newHeight?: number,
    newRim?: number
  ) => {
    if (!currentReading) return;

    // Mark the current reading as checked
    dispatch({ type: "MARK_CHECKED", index: state.currentIndex });

    setSelected(null);
    setAdjustmentReason(null);
    setAdjustedDepth(null);
    setMisreadWarning(false);

    // Move to next unchecked reading, or show loader if all checked
    const nextUnchecked = state.readings.findIndex(
      (r, i) => i > state.currentIndex && !r.checked
    );
    if (nextUnchecked !== -1) {
      dispatch({ type: "SET_INDEX", index: nextUnchecked });
    } else {
      dispatch({ type: "SET_INDEX", index: -1 });
    }
    console.log("Code:", code);
    console.log("Current reading:", currentReading.classifier_outcome);
    if (code === currentReading.classifier_outcome) {
      // Approve the machine reading
      await approveReading();
      return;
    }

    const payload: OverwriteType = {
      machine_read_id: currentReading.id,
      classifier_outcome: code!,
      reason: reason || "Verkeerde lezing",
    };

    if (!isNaN(newDepth as number)) payload.min_thread_depth = newDepth!;
    if (typeof newWidth === "number") payload.tire_width = newWidth;
    if (typeof newHeight === "number") payload.tire_height = newHeight;
    if (typeof newRim === "number") payload.rim_diameter = newRim;

    console.log("Sending overwrite to API:", payload);
    await createOverwriteRead(payload).catch(console.error);
  };

  const approveReading = async () => {
    if (!currentReading) return;
    try {
      console.log("Approving reading:", currentReading.id);
      await approveReadingInstant(currentReading.id);
    } catch (error) {
      console.error("Error approving reading:", error);
    }
  };

  return (
    <TireReadingContext.Provider
      value={{
        codes,
        selected,
        initial,
        adjustmentReason,
        adjustedDepth,
        currentReading,
        misreadWarning,
        connectionStatus,
        setCodes,
        confirmClassificationChange,
        setAdjustmentReason,
        setAdjustedDepth,
        setCurrentReading,
        setMisreadWarning,
        goToPreviousReading,
        goToNextReading,
        readings: state.readings,
        currentIndex: state.currentIndex,
        setCurrentIndex,
        approveReading,
      }}
    >
      {children}
    </TireReadingContext.Provider>
  );
};

export const useTireReading = (): TireReadingContextType => {
  const context = useContext(TireReadingContext);
  if (!context)
    throw new Error("useTireReading must be used within TireReadingProvider");
  return context;
};
